import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
  useVibesites,
} from '@vibesites/runtime/react';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface Grouped {
  key: string;
  category: string;
  items: Faq[];
}

const UNCATEGORIZED = '__uncategorized__';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim();
  if (s === '') return false;
  const lower = s.toLowerCase();
  return (
    lower !== 'false' &&
    lower !== 'null' &&
    lower !== 'undefined' &&
    lower !== 'n/a' &&
    lower !== 'none'
  );
}

function groupFaqs(items: Faq[]): Grouped[] {
  const bucket = new Map<string, Grouped>();
  const order: string[] = [];

  for (const faq of items) {
    if (!hasValue(faq?.question)) continue;
    const rawCategory = hasValue(faq.category) ? faq.category!.trim() : '';
    const key = rawCategory || UNCATEGORIZED;
    const label = rawCategory || 'General';
    if (!bucket.has(key)) {
      bucket.set(key, { key, category: label, items: [] });
      order.push(key);
    }
    bucket.get(key)!.items.push(faq);
  }

  return order.map((k) => bucket.get(k)!);
}

interface FaqAccordionItemProps {
  faq: Faq;
  rowId: string;
  defaultOpen?: boolean;
  answerHtml: string;
}

function FaqAccordionItem({ faq, rowId, defaultOpen = false, answerHtml }: FaqAccordionItemProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `${rowId}-panel`;
  const buttonId = `${rowId}-button`;

  return (
    <div className="faq-item">
      <h3 className="m-0">
        <button
          type="button"
          id={buttonId}
          aria-expanded={open}
          aria-controls={panelId}
          className="faq-trigger"
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="faq-question">{faq.question}</span>
          <span className="faq-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`faq-panel${open ? ' faq-panel--open' : ''}`}
        hidden={!open}
      >
        {answerHtml ? (
          <div
            className="faq-answer"
            dangerouslySetInnerHTML={{ __html: answerHtml }}
          />
        ) : (
          <p className="faq-answer faq-answer--empty">No answer provided yet.</p>
        )}
      </div>
    </div>
  );
}

export function FAQList() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const { property } = useVibesites();

  const faqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
  });

  const groups = useMemo(() => groupFaqs(faqs || []), [faqs]);
  const hasAnyCategory = useMemo(
    () => groups.some((g) => g.key !== UNCATEGORIZED),
    [groups],
  );
  const totalCount = useMemo(
    () => groups.reduce((sum, g) => sum + g.items.length, 0),
    [groups],
  );

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQ Items">
          {totalCount === 0 ? (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">FAQs will be displayed here.</p>
            </div>
          ) : (
            <div className="space-y-10 md:space-y-14">
              {groups.map((group, groupIndex) => (
                <div key={group.key}>
                  {hasAnyCategory && (
                    <h3 className="faq-category-heading">{group.category}</h3>
                  )}
                  <div className="faq-list">
                    {group.items.map((faq, itemIndex) => {
                      const rowId = `faq-${groupIndex}-${itemIndex}`;
                      const rawAnswer = typeof faq.answer === 'string' ? faq.answer : '';
                      const answerHtml = rawAnswer
                        ? resolveRichText(
                            rawAnswer,
                            property as Record<string, unknown>,
                          )
                        : '';
                      return (
                        <FaqAccordionItem
                          key={rowId}
                          rowId={rowId}
                          faq={faq}
                          defaultOpen={groupIndex === 0 && itemIndex === 0}
                          answerHtml={answerHtml}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-category-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-text);
          /* Centered on mobile, left-aligned once there's enough room for
             the heading to read as a section label alongside the cards. */
          text-align: center;
          padding-bottom: 0.6rem;
          margin: 0 0 1.25rem;
          border-bottom: 1px solid var(--vibe-color-border);
        }
        @media (min-width: 640px) {
          .faq-category-heading { text-align: left; }
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .faq-item {
          /* Hard-coded light surface so cards stay legible even when the
             property overrides --vibe-color-surface to a brand color. */
          background: #ffffff;
          border: 1px solid #e5e5e5;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .faq-item:hover {
          border-color: #bdbdbd;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
        }

        .faq-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.1rem 1.25rem;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          /* Dark text pinned to the white card below so property-level
             --vibe-color-text overrides can't make it unreadable. */
          color: #1a1a1a;
          letter-spacing: 0.02em;
        }
        .faq-trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .faq-question {
          flex: 1 1 auto;
          line-height: 1.4;
        }

        .faq-icon {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          color: #666666;
          transition: transform 0.25s ease;
        }
        .faq-icon svg {
          width: 1rem;
          height: 1rem;
        }
        .faq-trigger[aria-expanded="true"] .faq-icon {
          transform: rotate(180deg);
        }

        .faq-panel {
          padding: 0 1.25rem 1.1rem;
          border-top: 1px solid #ececec;
          animation: faqPanelIn 0.2s ease both;
        }
        .faq-panel[hidden] {
          display: none;
        }

        .faq-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: #333333;
          padding-top: 0.9rem;
        }
        .faq-answer p { margin: 0 0 0.75rem; }
        .faq-answer p:last-child { margin-bottom: 0; }
        .faq-answer ul,
        .faq-answer ol {
          margin: 0 0 0.75rem 1.25rem;
          padding-left: 0.5rem;
        }
        .faq-answer ul { list-style: disc; }
        .faq-answer ol { list-style: decimal; }
        .faq-answer li { margin-bottom: 0.35rem; }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer--empty {
          opacity: 0.7;
          font-style: italic;
        }

        @keyframes faqPanelIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .faq-panel { animation: none; }
          .faq-icon { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
